/**
 * Subscription Rules Engine
 * 
 * Pure functions for tier resolution, pricing calculations, and purchase validation.
 * No side effects - all functions are testable and reusable.
 * 
 * @module subscription-rules
 */

import { isPaidStatus, type UserSubscription, type SubscriptionStatus } from '@/types/subscription';

// ============================================
// Types
// ============================================

export type TierId = 'free' | 'pro' | 'business' | 'family';
export type BeneficiaryPolicy = 'self-only' | 'any';

export interface SubscriptionTier {
  id: TierId;
  name: string;
  canHaveSpecialPrice: boolean;
  ticketLimit: number | null; // null = unlimited
  beneficiaryPolicy: BeneficiaryPolicy;
}

export interface PricingConfig {
  markupPercentage: number; // Fallback: 0 (service-neutral; real markups come from DB rows — VOY-511)
  transactionFeePercentage: number; // Default: 0.04 (4%)
  paywallEnabled: boolean;
}

export interface PricingRules {
  tier: TierId;
  canHaveSpecialPrice: boolean;
  ticketLimit: number | null;
  beneficiaryPolicy: BeneficiaryPolicy;
  paywallEnabled: boolean;
  markupPercentage: number;
}

export interface PricingResult {
  standardPrice: number;
  specialPrice: number;
  savings: number;
  savingsPercentage: number;
  userCanPurchaseAtSpecialPrice: boolean;
  appliedMarkup: number;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  upgradeRequired?: boolean;
  suggestedTier?: TierId;
}

export interface UserProfile {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export interface PassengerInfo {
  firstName?: string;
  lastName?: string;
}

// ============================================
// Default Constants
// ============================================

export const DEFAULT_CONFIG: PricingConfig = {
  // VOY-511 (audit defect D5): the fallback markup must be SERVICE-NEUTRAL and fail-safe.
  // It is used only as a last resort when no `pricing_rules` row resolves (cold cache /
  // wrong scope / network error). A hotel value (0.25) here silently injected a 25% markup
  // onto flights on every such path. 0 never over-charges; real markups come from the DB
  // rows (`default/all`, `free/hotel`, ...), which the resolver reads first.
  markupPercentage: 0,
  transactionFeePercentage: 0.04,
  paywallEnabled: true,
};

export const DEFAULT_TIERS: Record<TierId, SubscriptionTier> = {
  free: {
    id: 'free',
    name: 'Free',
    canHaveSpecialPrice: false,
    ticketLimit: null,
    beneficiaryPolicy: 'any',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    canHaveSpecialPrice: true,
    ticketLimit: 8,
    beneficiaryPolicy: 'self-only',
  },
  business: {
    id: 'business',
    name: 'Business',
    canHaveSpecialPrice: true,
    ticketLimit: 8,
    beneficiaryPolicy: 'self-only',
  },
  family: {
    id: 'family',
    name: 'Family',
    canHaveSpecialPrice: true,
    ticketLimit: 8,
    beneficiaryPolicy: 'any',
  },
};

// VOY-554: integrity failures already reported, so a re-render does not log the same one again.
const reportedIntegrityFailures = new Set<string>();

// ============================================
// Pure Functions
// ============================================

/**
 * Get the subscription tier from a subscription object.
 * Paid status (active, trialing; not past_due) + the tier_id the stripe_user_subscriptions view
 * joins from subscription_prices (VOY-554). Price ids are never mapped here.
 * Returns Free tier on null, undefined, unpaid status, or error.
 *
 * DISPLAY ONLY: a paid subscription whose price is not in subscription_prices (tier_id null) is
 * logged as TIER_INTEGRITY and shown free-tier prices; checkout refuses it server-side
 * (409 TIER_UNRESOLVED), so that guess is never charged.
 */
export function getTierFromSubscription(
  subscription: UserSubscription | null | undefined
): SubscriptionTier {
  try {
    if (!subscription || !isPaidStatus(subscription.status)) {
      return DEFAULT_TIERS.free;
    }

    const tierId = subscription.tier_id;
    if (!tierId || !(tierId in DEFAULT_TIERS)) {
      const key = `${subscription.subscription_id}:${subscription.price_id}`;
      if (!reportedIntegrityFailures.has(key)) {
        reportedIntegrityFailures.add(key);
        console.error('[subscription-rules] TIER_INTEGRITY: paid subscription price is not in subscription_prices; showing free-tier prices', {
          subscriptionId: subscription.subscription_id,
          priceId: subscription.price_id,
        });
      }
      return DEFAULT_TIERS.free;
    }
    return DEFAULT_TIERS[tierId];
  } catch (error) {
    console.error('[subscription-rules] Error getting tier from subscription:', error);
    return DEFAULT_TIERS.free;
  }
}

/**
 * Fields a pricing_rules tier row may override (VOY-606). Markup comes from the product
 * config (the backend charges from it), and paywall_enabled only from the `default` row:
 * tier rows carry paywall_enabled=false, which must not switch special pricing off.
 */
export const TIER_OVERRIDE_FIELDS = ['canHaveSpecialPrice', 'ticketLimit', 'beneficiaryPolicy'] as const;

function pickTierOverride(override: Partial<PricingRules>): Partial<PricingRules> {
  const picked: Partial<PricingRules> = {};
  for (const field of TIER_OVERRIDE_FIELDS) {
    const value = override[field];
    // null means "unlimited" for ticketLimit; for the other fields a null DB value is
    // missing data and must not replace the tier default
    if (value === undefined || (value === null && field !== 'ticketLimit')) continue;
    (picked as Record<string, unknown>)[field] = value;
  }
  return picked;
}

/**
 * Derive pricing rules from subscription and config.
 * A tier override (DB row for the user's tier) may only change TIER_OVERRIDE_FIELDS.
 */
export function derivePricingRules(
  subscription: UserSubscription | null | undefined,
  config: PricingConfig,
  context?: { hotelId?: string; category?: string; tierOverride?: Partial<PricingRules> }
): PricingRules {
  const tier = getTierFromSubscription(subscription);
  
  // Base rules from tier
  let rules: PricingRules = {
    tier: tier.id,
    canHaveSpecialPrice: tier.canHaveSpecialPrice,
    ticketLimit: tier.ticketLimit,
    beneficiaryPolicy: tier.beneficiaryPolicy,
    paywallEnabled: config.paywallEnabled,
    markupPercentage: config.markupPercentage,
  };

  // Apply the tier override first, so the global paywall switch below always wins
  if (context?.tierOverride) {
    rules = { ...rules, ...pickTierOverride(context.tierOverride) };
  }

  // If paywall is disabled, no one gets special pricing
  if (!config.paywallEnabled) {
    rules.canHaveSpecialPrice = false;
  }

  return rules;
}

/**
 * Calculate standard and special prices from API price
 * 
 * Formula:
 * - Standard = API_Price + (API_Price * markup) + (Total * transactionFee)
 * - Special = API_Price + (API_Price * transactionFee)
 */
export function calculatePricing(
  apiPrice: number,
  rules: PricingRules,
  config: PricingConfig = DEFAULT_CONFIG
): PricingResult {
  const markup = rules.markupPercentage ?? config.markupPercentage;
  const transactionFee = config.transactionFeePercentage;

  // Standard price: API + markup + transaction fee on total
  const withMarkup = apiPrice * (1 + markup);
  const standardPrice = withMarkup * (1 + transactionFee);

  // Special price: API + transaction fee only (no markup)
  const specialPrice = apiPrice * (1 + transactionFee);

  const savings = standardPrice - specialPrice;
  const savingsPercentage = standardPrice > 0 ? (savings / standardPrice) * 100 : 0;

  return {
    standardPrice: Math.round(standardPrice * 100) / 100, // Round to 2 decimals
    specialPrice: Math.round(specialPrice * 100) / 100,
    savings: Math.round(savings * 100) / 100,
    savingsPercentage: Math.round(savingsPercentage * 10) / 10, // Round to 1 decimal
    userCanPurchaseAtSpecialPrice: rules.canHaveSpecialPrice && rules.paywallEnabled,
    appliedMarkup: markup,
  };
}

/**
 * Validate if a purchase can proceed based on tier rules
 * Checks ticket limits and beneficiary restrictions
 */
export function canPurchase(
  rules: PricingRules,
  itemCount: number,
  beneficiaries: PassengerInfo[],
  userProfile: UserProfile | null
): ValidationResult {
  // Check ticket limit
  if (rules.ticketLimit !== null && itemCount > rules.ticketLimit) {
    return {
      isValid: false,
      error: `Maximum ${rules.ticketLimit} passengers allowed per booking on your ${rules.tier} plan.`,
      upgradeRequired: false,
    };
  }

  // Check beneficiary restriction for self-only tiers
  if (rules.beneficiaryPolicy === 'self-only' && rules.canHaveSpecialPrice) {
    // If no profile, they can't verify self-only
    if (!userProfile || (!userProfile.firstName && !userProfile.lastName)) {
      return {
        isValid: false,
        error: 'Please complete your profile to book at special prices.',
        upgradeRequired: false,
      };
    }

    // Check if any beneficiary is not the user
    const userFirstName = (userProfile.firstName || '').toLowerCase().trim();
    const userLastName = (userProfile.lastName || '').toLowerCase().trim();

    const invalidBeneficiaries = beneficiaries.filter((p) => {
      const pFirstName = (p.firstName || '').toLowerCase().trim();
      const pLastName = (p.lastName || '').toLowerCase().trim();
      return pFirstName !== userFirstName || pLastName !== userLastName;
    });

    if (invalidBeneficiaries.length > 0) {
      return {
        isValid: false,
        error: `Your ${rules.tier} plan only allows bookings in your own name (${userProfile.firstName} ${userProfile.lastName}). Upgrade to Family to book for others.`,
        upgradeRequired: true,
        suggestedTier: 'family',
      };
    }
  }

  return { isValid: true };
}

/**
 * Validate if an item can be added to cart at a specific price type
 */
export function validatePriceType(
  rules: PricingRules,
  priceType: 'standard' | 'special'
): ValidationResult {
  if (priceType === 'special' && !rules.canHaveSpecialPrice) {
    return {
      isValid: false,
      error: 'Special pricing requires an active subscription.',
      upgradeRequired: true,
      suggestedTier: 'pro',
    };
  }

  return { isValid: true };
}

/**
 * Get the default tier (Free)
 */
export function getDefaultTier(): SubscriptionTier {
  return DEFAULT_TIERS.free;
}

/**
 * Get tier by ID
 */
export function getTierById(tierId: TierId): SubscriptionTier {
  return DEFAULT_TIERS[tierId] || DEFAULT_TIERS.free;
}

/**
 * Check if a subscription status grants special price access
 */
export function hasActiveSubscription(status: SubscriptionStatus | null | undefined): boolean {
  return isPaidStatus(status);
}


